import React from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../../elements/ui/card';
import EmptyState from './EmptyState';
import { useTranslation } from 'react-i18next';
import { formatTimeLabel, hasValues, mergeSeries, timeAxisProps, type TimeRange } from './timeSeries';
import type { LineSeries } from '../../../models/time-series';

interface DualAxisLineChartProps {
  title: string;
  series1: LineSeries[];
  unit1?: string;
  series2: LineSeries[];
  unit2?: string;
  range: TimeRange;
}

function CustomTooltip({ active, payload, label, unit1, unit2, series1Names }: any) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-medium text-gray-700 mb-1">{formatTimeLabel(label)}</p>
      {payload.map((entry: any) => {
        const isLeft = series1Names.includes(entry.name);
        const unit = isLeft ? unit1 : unit2;

        return (
          <div key={entry.name} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.stroke }} />
            <span className="text-gray-600">{entry.name}:</span>
            <span className="font-medium text-gray-900">
              {entry.value != null ? `${entry.value} ${unit || ''}` : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const DualAxisLineChart: React.FC<DualAxisLineChartProps> = ({
  title,
  series1,
  unit1 = 'ppm',
  series2,
  unit2 = '°C',
  range
}) => {
  const { t } = useTranslation();

  const allSeries = React.useMemo(() => [...series1, ...series2], [series1, series2]);
  const mergedData = React.useMemo(() => mergeSeries(allSeries, range), [allSeries, range]);
  const hasData = hasValues(allSeries);

  const series1Names = series1.map((s) => s.name);

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <div className="flex items-center gap-3">
            {series1.map((s) => (
              <div key={s.name} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.strokeColor }} />
                <span className="text-xs text-gray-500">
                  {s.name} ({unit1})
                </span>
              </div>
            ))}
            {series2.map((s) => (
              <div key={s.name} className="flex items-center gap-1.5">
                <div className="w-4 h-0 border-t-2 border-dashed" style={{ borderColor: s.strokeColor }} />
                <span className="text-xs text-gray-500">
                  {s.name} ({unit2})
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <EmptyState
            title={`${t('f-no-data-placeholder', { value: title.toUpperCase() })}`}
            description={`${t('f-sensor-not-available')}`}
          />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={mergedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis {...timeAxisProps(range)} />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <Tooltip content={<CustomTooltip unit1={unit1} unit2={unit2} series1Names={series1Names} />} />
              {series1.map((s) => (
                <Line
                  key={s.name}
                  yAxisId="left"
                  type="monotone"
                  dataKey={s.name}
                  stroke={s.strokeColor}
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
              ))}
              {series2.map((s) => (
                <Line
                  key={s.name}
                  yAxisId="right"
                  type="monotone"
                  dataKey={s.name}
                  stroke={s.strokeColor}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  connectNulls={false}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default DualAxisLineChart;
